/**
 * usePerspectives Hook
 * Custom hook for managing perspectives data and CRUD operations
 * Follows React best practices with proper error handling and loading states
 */

import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@shared/context/AuthContext";
import { Perspective } from "../types/perspective.types";
import * as PerspectiveService from "../services/PerspectiveService";
import { PerspectiveThumbnailService } from "../services/PerspectiveThumbnailService";
import logger from "@shared/utils/logger";

interface UsePerspectivesReturn {
  perspectives: Perspective[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  createPerspective: (data: Partial<Perspective>) => Promise<Perspective>;
  updatePerspective: (
    id: string,
    data: Partial<Perspective>,
  ) => Promise<Perspective>;
  deletePerspective: (id: string) => Promise<void>;
  duplicatePerspective: (id: string) => Promise<Perspective>;
  sharePerspective: (
    id: string,
    scope: "private" | "team" | "system",
    recipients?: string[],
  ) => Promise<void>;
  savePerspectiveWithThumbnail: (
    perspective: Partial<Perspective>,
    sourceCanvas?: HTMLCanvasElement,
    isClinicalMode?: boolean,
  ) => Promise<Perspective>;
  canEdit: (perspectiveId: string) => boolean;
  canShare: (perspectiveId: string) => boolean;
}

function sortPerspectives(items: Perspective[]): Perspective[] {
  return [...items].sort((a, b) => {
    if (a.is_system && !b.is_system) return -1;
    if (!a.is_system && b.is_system) return 1;

    const nameA = a?.perspective_name ?? "";
    const nameB = b?.perspective_name ?? "";
    return nameA.localeCompare(nameB);
  });
}

/**
 * Hook for managing perspectives data
 * Provides CRUD operations and loading/error states
 *
 * @returns {UsePerspectivesReturn} Perspectives data and operations
 *
 * @example
 * const { perspectives, loading, error, refetch, deletePerspective } = usePerspectives();
 */
export const usePerspectives = (): UsePerspectivesReturn => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const updatePerspectiveQueryData = useCallback(
    (updater: (current: Perspective[]) => Perspective[]) => {
      queryClient.setQueryData<Perspective[]>(
        ["perspectives", user?.id],
        (current) => {
          const currentPerspectives = Array.isArray(current) ? current : [];
          return sortPerspectives(updater(currentPerspectives));
        },
      );
    },
    [queryClient, user?.id],
  );

  /**
   * Fetch all perspectives from API
   * Combines system presets and user custom perspectives
   */
  const {
    data: perspectives = [],
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ["perspectives", user?.id],
    queryFn: async () => {
      const customData = await PerspectiveService.fetchPerspectives();
      const safeData = Array.isArray(customData) ? customData : [];
      return sortPerspectives(safeData);
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const error =
    queryError instanceof Error
      ? queryError.message
      : queryError
        ? "Failed to load perspectives. Please try again."
        : null;

  const fetchPerspectives = useCallback(async (): Promise<void> => {
    try {
      await refetch();
    } catch (err) {
      logger.error("Failed to fetch perspectives:", err);
      throw err;
    }
  }, [refetch]);

  /**
   * Create a new perspective
   * @param data - Partial perspective data
   * @returns Created perspective
   */
  const createPerspective = useCallback(
    async (data: Partial<Perspective>): Promise<Perspective> => {
      try {
        const created = await PerspectiveService.createPerspective(data);

        updatePerspectiveQueryData((prev) => [...prev, created]);

        return created;
      } catch (err) {
        logger.error("Failed to create perspective:", err);
        throw new Error(
          err instanceof Error
            ? err.message
            : "Failed to create perspective. Please try again.",
        );
      }
    },
    [updatePerspectiveQueryData],
  );

  /**
   * Update an existing perspective
   * @param id - Perspective ID
   * @param data - Partial perspective data to update
   * @returns Updated perspective
   */
  const updatePerspective = useCallback(
    async (id: string, data: Partial<Perspective>): Promise<Perspective> => {
      try {
        const updated = await PerspectiveService.updatePerspective(id, data);

        updatePerspectiveQueryData((prev) =>
          prev.map((p) => (p.perspective_id === id ? updated : p)),
        );

        return updated;
      } catch (err) {
        logger.error("Failed to update perspective:", err);
        throw new Error(
          err instanceof Error
            ? err.message
            : "Failed to update perspective. Please try again.",
        );
      }
    },
    [updatePerspectiveQueryData],
  );

  /**
   * Delete a perspective
   * @param id - Perspective ID to delete
   */
  const deletePerspective = useCallback(async (id: string): Promise<void> => {
    try {
      await PerspectiveService.deletePerspective(id);

      updatePerspectiveQueryData((prev) =>
        prev.filter((p) => p.perspective_id !== id),
      );
    } catch (err) {
      logger.error("Failed to delete perspective:", err);
      throw new Error(
        err instanceof Error
          ? err.message
          : "Failed to delete perspective. Please try again.",
      );
    }
  }, [updatePerspectiveQueryData]);

  /**
   * Save perspective with thumbnail generation
   * @param perspective - Perspective data to save
   * @param sourceCanvas - Optional canvas element to generate thumbnail from
   * @param isClinicalMode - Whether in clinical mode for watermarking
   * @returns Saved perspective
   */
  const savePerspectiveWithThumbnail = useCallback(
    async (
      perspective: Partial<Perspective>,
      sourceCanvas?: HTMLCanvasElement,
      isClinicalMode: boolean = false,
    ): Promise<Perspective> => {
      try {
        let thumbnailDataUrl: string | undefined;

        // Generate thumbnail if canvas provided
        if (sourceCanvas) {
          const thumbnail = await PerspectiveThumbnailService.generateThumbnail(
            sourceCanvas,
            {
              width: 320,
              height: 80,
              isClinicalMode,
              montageName: perspective.montage_id || "Unknown",
              timestamp: new Date(),
            },
          );
          thumbnailDataUrl = thumbnail.dataUrl;
        }

        // Create perspective with thumbnail and version metadata
        const saved = await PerspectiveService.createPerspective({
          ...perspective,
          thumbnail_data: thumbnailDataUrl,
          schema_version: "2.0",
          created_at: new Date().toISOString(),
        });

        updatePerspectiveQueryData((prev) => [...prev, saved]);

        return saved;
      } catch (err) {
        logger.error("Failed to save perspective with thumbnail:", err);
        throw new Error(
          err instanceof Error
            ? err.message
            : "Failed to save perspective. Please try again.",
        );
      }
    },
    [updatePerspectiveQueryData],
  );

  /**
   * Duplicate a perspective
   * Creates a copy with "(Copy)" appended to name
   * @param id - Perspective ID to duplicate
   * @returns Duplicated perspective
   */
  const duplicatePerspective = useCallback(
    async (id: string): Promise<Perspective> => {
      try {
        const original = perspectives.find((p) => p.perspective_id === id);

        if (!original) {
          throw new Error("Perspective not found");
        }

        // Create duplicate with modified name and version tracking
        const duplicate = await PerspectiveService.createPerspective({
          ...original,
          perspective_id: undefined, // Remove ID to create new
          perspective_name: `${original.perspective_name} (Copy)`,
          is_default: false, // Don't copy default status
          created_at: undefined, // Will be set by server
          updated_at: undefined,
          schema_version: "2.0",
          duplicated_from: original.perspective_id,
          thumbnail_data: original.thumbnail_data, // Preserve thumbnail
        });

        updatePerspectiveQueryData((prev) => [...prev, duplicate]);

        return duplicate;
      } catch (err) {
        logger.error("Failed to duplicate perspective:", err);
        throw new Error(
          err instanceof Error
            ? err.message
            : "Failed to duplicate perspective. Please try again.",
        );
      }
    },
    [perspectives, updatePerspectiveQueryData],
  );

  /**
   * Share a perspective with specified scope
   * @param id - Perspective ID to share
   * @param scope - Sharing scope: 'private', 'team', or 'system'
   * @param recipients - Optional array of user IDs for team sharing
   */
  const sharePerspective = useCallback(
    async (
      id: string,
      scope: "private" | "team" | "system",
      recipients?: string[],
    ): Promise<void> => {
      try {
        await PerspectiveService.sharePerspective(id, { scope, recipients });

        updatePerspectiveQueryData((prev) =>
          prev.map((p) => {
            if (p.perspective_id === id) {
              return {
                ...p,
                is_shared: scope !== "private",
                is_system: scope === "system",
              };
            }
            return p;
          }),
        );
      } catch (err) {
        logger.error("Failed to share perspective:", err);
        throw new Error(
          err instanceof Error
            ? err.message
            : "Failed to share perspective. Please try again.",
        );
      }
    },
    [updatePerspectiveQueryData],
  );

  /**
   * Check if user can edit a perspective
   * System perspectives cannot be edited
   * @param perspectiveId - Perspective ID to check
   * @returns true if user can edit
   */
  const canEdit = useCallback(
    (perspectiveId: string): boolean => {
      const perspective = perspectives.find(
        (p) => p.perspective_id === perspectiveId,
      );
      return !perspective?.is_system;
    },
    [perspectives],
  );

  /**
   * Check if user can share a perspective
   * System perspectives cannot be shared
   * @param perspectiveId - Perspective ID to check
   * @returns true if user can share
   */
  const canShare = useCallback(
    (perspectiveId: string): boolean => {
      const perspective = perspectives.find(
        (p) => p.perspective_id === perspectiveId,
      );
      return !perspective?.is_system;
    },
    [perspectives],
  );

  return {
    perspectives,
    loading,
    error,
    refetch: fetchPerspectives,
    createPerspective,
    updatePerspective,
    deletePerspective,
    duplicatePerspective,
    sharePerspective,
    savePerspectiveWithThumbnail,
    canEdit,
    canShare,
  };
};

export default usePerspectives;
